/* Heart-rate zones from second-by-second heart rate, for runs whose watch didn't write its own
 * time-in-zone summary (files Strava rebuilt, Apple Health, …). Each run keeps a histogram of seconds
 * at each bpm, so zones can be worked out once the max heart rate is known for the whole dataset. */

export interface HrProfile { hist: Map<number, number>; secs: number; max30: number | null }

/* samples: [seconds, bpm], in time order. Each sample counts until the next one, capped at 10 s so
 * pauses and dropouts don't count. max30 is the highest 30-second average: a single spike can't set it. */
export function hrProfile(samples: [number, number][]): HrProfile | null {
  const hist = new Map<number, number>();
  let secs = 0;
  const n = samples.length;
  for (let i = 0; i < n; i++) {
    const [t, bpm] = samples[i];
    if (!(bpm >= 30 && bpm <= 240)) continue;
    const dt = i + 1 < n ? Math.min(10, Math.max(0, samples[i + 1][0] - t)) : 1;
    if (!dt) continue;
    const b = Math.round(bpm);
    hist.set(b, (hist.get(b) || 0) + dt);
    secs += dt;
  }
  if (secs < 60) return null;
  // highest average over any 30 s stretch (two pointers over the samples)
  let max30: number | null = null, j = 0;
  for (let i = 0; i < n; i++) {
    while (samples[i][0] - samples[j][0] > 30) j++;
    if (samples[i][0] - samples[j][0] < 25 || i - j < 2) continue;
    let s = 0;
    for (let k = j; k <= i; k++) s += samples[k][1];
    const avg = s / (i - j + 1);
    if (avg <= 230 && (max30 === null || avg > max30)) max30 = avg;
  }
  return { hist, secs, max30: max30 === null ? null : Math.round(max30) };
}

/* Share of time in zones 1–5. bounds are the upper edges of zones 0–5 in bpm (as watches store them);
 * time below zone 1 is folded into zone 1, and time above the top edge into zone 5. */
export function zonesFrom(p: HrProfile, bounds: number[]): number[] | null {
  const z = [0, 0, 0, 0, 0];
  for (const [bpm, s] of p.hist) {
    let i = bounds.slice(1, 5).findIndex((b) => bpm <= b);
    if (i < 0) i = 4;
    z[i] += s;
  }
  const tot = z.reduce((a, b) => a + b, 0);
  return tot ? z.map((x) => x / tot) : null;
}

/* The histogram kept on a run (seconds at each bpm from lo upwards), so the report can redo the zones
 * when the runner enters their own max heart rate. */
export interface PackedHist { lo: number; s: number[] }
export function packHist(p: HrProfile): PackedHist {
  const bpms = [...p.hist.keys()], lo = Math.min(...bpms), hi = Math.max(...bpms);
  const s = new Array<number>(hi - lo + 1).fill(0);
  for (const [b, v] of p.hist) s[b - lo] = Math.round(v);
  return { lo, s };
}
export function unpackHist(h: PackedHist): HrProfile {
  const hist = new Map<number, number>();
  h.s.forEach((v, i) => { if (v) hist.set(h.lo + i, v); });
  return { hist, secs: h.s.reduce((a, b) => a + b, 0), max30: null };
}

/* Zones as % of max heart rate (50/60/70/80/90/100), the bands the report describes. */
export const pctBounds = (maxHr: number) => [0.5, 0.6, 0.7, 0.8, 0.9, 1].map((f) => Math.round(f * maxHr));

/* Max heart rate estimated from the runs: the highest 30-second average in any run. A runner's true max
 * is often higher (easy runners rarely reach it), which the report says. Needs 10+ runs with heart rate. */
export function estimateMaxHr(profiles: HrProfile[]): number | null {
  const tops = profiles.map((p) => p.max30).filter((x): x is number => x !== null).sort((a, b) => b - a);
  if (tops.length < 10) return null;
  // the top value must be backed by another run within 8 bpm; otherwise it's treated as a glitch
  for (let i = 0; i + 1 < tops.length; i++) if (tops[i] - tops[i + 1] <= 8) return tops[i];
  return null;
}
